'use client';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useChart } from '@/contexts/chart-context';
import { useToast } from '@/hooks/use-toast';
import { apiClient } from '@/lib/api-client';
import { FUEL_COLORS, FUEL_LABELS, type FuelType } from '@/constants/fuel-colors';
import { DomesticOilPrice } from '@/lib/api/data-contracts';
import HighchartsReact from 'highcharts-react-official';
import { useCallback, useEffect, useRef, useState } from 'react';
import { unwrap } from '@/lib/unwrap';

// 1년 전 날짜 계산
const getOneYearAgo = (): string => {
    const date = new Date();
    date.setFullYear(date.getFullYear() - 1);
    return date.toISOString().split('T')[0].replace(/-/g, '');
};

// 오늘 날짜
const getToday = (): string => {
    return new Date().toISOString().split('T')[0].replace(/-/g, '');
};

// 국내 유가 라벨 (OPINET 기준 '보통휘발유' 사용)
const DOMESTIC_LABELS: Record<FuelType, string> = {
    ...FUEL_LABELS,
    gas: '보통휘발유',
};

// 연도 변경 구간 plotLines 생성
const getYearChangePlotLines = (data: { priceDate?: string }[]) => {
    const years = new Set<number>();
    const plotLines: any[] = [];

    data.forEach(d => {
        if (d.priceDate) {
            const date = new Date(d.priceDate);
            years.add(date.getFullYear());
        }
    });

    // 각 연도의 1월 1일에 세로선 추가
    years.forEach(year => {
        const yearStart = new Date(year, 0, 1).getTime();
        plotLines.push({
            color: '#888888',
            dashStyle: 'Dash',
            value: yearStart,
            width: 1,
            label: {
                text: `${year}년`,
                align: 'left',
                rotation: 0,
                y: 15,
                style: {
                    color: '#666666',
                    fontSize: '11px',
                },
            },
            zIndex: 5,
        });
    });

    return plotLines;
};

/**
 * 국내 유가 차트 컴포넌트
 */
export function DomesticOilPriceChart() {
    const { Highcharts, isHighchartsLoaded } = useChart();
    const { toast } = useToast();
    const [domesticData, setDomesticData] = useState<DomesticOilPrice[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const chartRef = useRef<HighchartsReact.RefObject>(null);

    const loadData = useCallback(async () => {
        if (!isHighchartsLoaded || !Highcharts) return;

        try {
            setIsLoading(true);

            const startDate = getOneYearAgo();
            const endDate = getToday();

            const response = await apiClient.get('opinet/domestic-oil-price', {
                searchParams: { startDate, endDate },
            });
            const result = unwrap((await response.json()) as DomesticOilPrice[]);
            setDomesticData(result || []);
        } catch {
            toast({
                title: '국내 유가 데이터 로드 실패',
                description: '국내 유가 데이터를 불러오는 중 오류가 발생했습니다',
                variant: 'destructive',
            });
        } finally {
            setIsLoading(false);
        }
    }, [isHighchartsLoaded, Highcharts, toast]);

    useEffect(() => {
        if (isHighchartsLoaded && Highcharts) {
            loadData();
        }
    }, [isHighchartsLoaded, Highcharts, loadData]);

    const chartOptions = {
        chart: {
            type: 'spline',
            zoomType: 'x',
            height: 300,
        },
        title: {
            text: '',
        },
        xAxis: {
            type: 'datetime',
            title: { text: '' },
            dateTimeLabelFormats: {
                month: '%m월',
                year: '%Y년',
            },
            tickInterval: 30 * 24 * 3600 * 1000,
            plotLines: getYearChangePlotLines(domesticData),
        },
        yAxis: {
            title: {
                text: '원/L',
            },
            labels: {
                format: '{value:,.0f}',
            },
            startOnTick: false,
            endOnTick: false,
        },
        tooltip: {
            shared: true,
            xDateFormat: '%Y-%m-%d',
            valueSuffix: ' 원/L',
        },
        legend: {
            layout: 'horizontal',
            align: 'center',
            verticalAlign: 'bottom',
        },
        series: (Object.keys(DOMESTIC_LABELS) as FuelType[]).map(key => ({
            name: DOMESTIC_LABELS[key],
            color: FUEL_COLORS[key].primary,
            data: domesticData
                .filter(d => d.priceDate && d[key as keyof DomesticOilPrice] != null)
                .map(d => [
                    new Date(d.priceDate!).getTime(),
                    d[key as keyof DomesticOilPrice] as number,
                ])
                .sort((a, b) => a[0] - b[0]),
        })),
        exporting: { enabled: false },
        credits: { enabled: false },
    };

    return (
        <Card>
            <CardHeader>
                <CardTitle>국내 유가 동향 (최근 1년)</CardTitle>
            </CardHeader>
            <CardContent>
                {!isHighchartsLoaded || isLoading ? (
                    <div className='flex items-center justify-center h-[300px]'>
                        <div className='text-gray-500'>
                            {!isHighchartsLoaded
                                ? 'Highcharts 초기화 중...'
                                : '데이터 로딩 중...'}
                        </div>
                    </div>
                ) : domesticData.length === 0 ? (
                    <div className='flex items-center justify-center h-[300px]'>
                        <div className='text-gray-500'>데이터가 없습니다</div>
                    </div>
                ) : (
                    <HighchartsReact
                        ref={chartRef}
                        highcharts={Highcharts}
                        options={chartOptions}
                    />
                )}
            </CardContent>
        </Card>
    );
}
